<template>
  <div class="pagina">
    <!-- Antes esto era un v-alert naranja que decia "Esta pagina no existe o
         no esta disponible" para los tres casos a la vez, sobre una pagina
         vacia. Ahora se distinguen, porque la API si los diferencia: 404 es
         que no existe, 403 que existe pero el visitante no puede verla, y un
         200 con isEnabled en false solo lo ve el dueno. -->
    <div
      v-if="estado === 'cargando'"
      class="estado-vacio"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="34"
        width="3"
      />
    </div>

    <div
      v-else-if="estado !== 'ok'"
      class="estado-vacio"
    >
      <div
        class="tarjeta-estado"
        :class="{ 'tarjeta-dueno': estado === 'sinVigenciaDueno' }"
      >
        <div
          class="icono-estado"
          :class="{ 'icono-aviso': estado === 'sinVigenciaDueno' }"
        >
          <v-icon size="30">
            {{ estado === 'noExiste' ? 'mdi-link-variant-off' : 'mdi-store-clock-outline' }}
          </v-icon>
        </div>

        <template v-if="estado === 'noExiste'">
          <h1 class="titulo-estado">
            No encontramos esta página
          </h1>
          <p class="texto-estado">
            El enlace puede estar mal escrito, o esta empresa ya no está en Bahiol.
          </p>
        </template>

        <template v-else-if="estado === 'sinVigencia'">
          <h1 class="titulo-estado">
            Esta empresa no está disponible
          </h1>
          <p class="texto-estado">
            Su publicación en Bahiol no está vigente en este momento. Si conoces
            el negocio, puede que vuelva a aparecer pronto.
          </p>
        </template>

        <template v-else>
          <h1 class="titulo-estado">
            Tu empresa no se está viendo
          </h1>
          <p class="texto-estado">
            <strong>{{ company ? company.company : '' }}</strong> no aparece para
            nadie más porque su hospedaje no está vigente. Renuévalo y vuelve a
            salir en el directorio, en las búsquedas y en este enlace.
          </p>
          <p class="nota-estado">
            Solo tú ves este aviso. Un visitante ve que la página no está disponible.
          </p>
        </template>

        <div class="acciones-estado">
          <template v-if="estado === 'sinVigenciaDueno'">
            <v-btn
              color="primary"
              variant="flat"
              @click="$router.push({ name: 'MyPaymentsView' })"
            >
              <v-icon start>
                mdi-credit-card-outline
              </v-icon>
              Renovar hospedaje
            </v-btn>
            <v-btn
              variant="text"
              @click="$router.push({ name: 'EditCompanyView', params: { id: idCompany } })"
            >
              Editar empresa
            </v-btn>
          </template>
          <v-btn
            v-else
            color="primary"
            variant="flat"
            @click="$router.push({ name: 'CompanysView' })"
          >
            <v-icon start>
              mdi-store-search-outline
            </v-icon>
            Ver otras empresas
          </v-btn>
        </div>
      </div>
    </div>

    <template v-if="estado === 'ok'">
      <div class="contenido-empresa">
      <!-- COLUMNA IZQUIERDA: la empresa -->
      <div class="columna">
      <!-- CAROUSEL -->
      <v-carousel
        :key="images.length"
        progress="primary"
        show-arrows="hover"
        hide-delimiter-background
        class="carousel"
      >
        <v-carousel-item
          v-for="image in images"
          :key="image.idImagen"
          :src="image.pathBase64"
          cover
        />
      </v-carousel>

      <!-- GALERIA: hasta 5 imagenes. Con menos de 5 salen todas; si hay mas,
           la ultima lleva un +N para que se note que quedan por ver -->
      <div
        v-if="imagenesGaleria.length > 0"
        class="seccion-galeria"
      >
        <div class="titulo-galeria">Fotos</div>
        <div class="grilla-galeria">
          <div
            v-for="(imagen, indice) in imagenesGaleria"
            :key="imagen.idImagen"
            class="celda-galeria"
            @click="abrirImagen(indice)"
          >
            <v-img
              :src="imagen.pathBase64"
              cover
              class="foto-galeria"
            />
            <div
              v-if="indice === imagenesGaleria.length - 1 && fotosRestantes > 0"
              class="mas-fotos"
            >
              +{{ fotosRestantes }}
            </div>
          </div>
        </div>
      </div>


      <!-- INFO EMPRESA -->
      <div class="seccion-info">
        <div class="nombre-empresa">{{ company ? company.company : '' }}</div>
        <div class="chips">
          <v-chip v-if="company && company.city" size="small" class="chip">
            <v-icon start size="14">mdi-map-marker</v-icon>
            {{ company.city }}
          </v-chip>
          <v-chip v-if="company && company.country" size="small" class="chip">
            <v-icon start size="14">mdi-earth</v-icon>
            {{ company.country }}
          </v-chip>
          <v-chip v-if="company && company.acceptsReservations" size="small" class="chip-reservas">
            <v-icon start size="14">mdi-calendar-check</v-icon>
            Acepta reservas
          </v-chip>
        </div>
        <p class="descripcion">{{ company ? company.description : '' }}</p>

        <!-- INFO DE RESERVAS: solo si la empresa las acepta. Si no, no se
             muestra nada, porque a la mayoria de negocios no les aplica -->
        <div
          v-if="company && company.acceptsReservations"
          class="aviso-reservas"
        >
          <v-icon class="icono-reservas" size="20">mdi-calendar-check</v-icon>
          <div>
            <div class="titulo-reservas">Reservas</div>
            <div class="texto-reservas">
              Este negocio recibe reservas. Comunícate con ellos para apartar tu espacio.
            </div>
          </div>
        </div>

        <!-- BOTONES DE ACCIÓN -->
        <div class="acciones" v-if="company">
          <!-- Llamar y WhatsApp van solo con icono: son universales y, con
               cuatro botones, el texto dejaba las etiquetas ilegibles en
               celular. Llevan title y aria-label para quien no ve el icono. -->
          <v-bottom-sheet>
            <template #activator="{ props: activatorProps }">
              <v-btn
                class="bg-blue btn-icono"
                v-bind="activatorProps"
                icon
                title="Llamar"
                aria-label="Llamar"
              >
                <v-icon>mdi-phone</v-icon>
              </v-btn>
            </template>
            <v-card class="pa-4">
              <v-card-title>¿Llamar a {{ company.phone }}?</v-card-title>
              <v-card-actions>
                <v-btn class="bg-blue text-white" @click="callCompany">Llamar</v-btn>
              </v-card-actions>
            </v-card>
          </v-bottom-sheet>

          <!-- Solo si la empresa dijo que recibe WhatsApp Y dejo el numero -->
          <v-btn
            v-if="tieneWhatsapp"
            class="btn-icono btn-whatsapp"
            icon
            title="Escribir por WhatsApp"
            aria-label="Escribir por WhatsApp"
            @click="abrirWhatsapp"
          >
            <v-icon>mdi-whatsapp</v-icon>
          </v-btn>

          <v-bottom-sheet>
            <template #activator="{ props: activatorProps }">
              <v-btn class="bg-green btn-accion" v-bind="activatorProps">
                <v-icon class="mr-1">mdi-google-maps</v-icon>
                Ubicación
              </v-btn>
            </template>
            <v-card class="pa-4">
              <v-card-title>{{ company.company }}</v-card-title>
              <v-card-text>
                <p><strong>Ciudad:</strong> {{ company.city }}</p>
                <p><strong>Dirección:</strong> {{ company.location }}</p>
                <p><strong>Teléfono:</strong> {{ company.phone }}</p>
              </v-card-text>
              <v-card-actions>
                <v-btn class="bg-green text-white" @click="toMapsDireccion">
                  <v-icon class="mr-1">mdi-open-in-new</v-icon>
                  Ver en Maps
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-bottom-sheet>

          <!-- Compartir: en celular abre el menu nativo del sistema, y donde
               no exista se copia el enlace al portapapeles -->
          <v-btn class="btn-accion btn-compartir" @click="compartir">
            <v-icon class="mr-1">mdi-share-variant</v-icon>
            Compartir
          </v-btn>
        </div>
      </div>

      </div>

      <!-- COLUMNA DERECHA: el catalogo -->
      <div class="columna">
      <!-- PRODUCTOS -->
      <div class="seccion-productos">
        <div class="titulo-productos">Productos y Servicios</div>
        <div v-if="products.length === 0" class="sin-productos">
          Esta empresa aún no tiene productos registrados
        </div>
        <div class="grilla-productos">
          <ComponentProduct
            v-for="product in products"
            :key="product.idProduct"
            :product="product"
            class="producto-item"
            @click="$router.push({ name: 'ViewProductView', params: { id: product.idProduct } })"
          />
        </div>
      </div>
      </div>
      </div>
    </template>

    <!-- Visor: al tocar una foto se ve completa -->
    <v-dialog
      v-model="visorAbierto"
      max-width="900"
    >
      <v-card class="tarjeta-visor">
        <v-btn
          icon
          variant="flat"
          size="small"
          class="cerrar-visor"
          @click="visorAbierto = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-carousel
          v-model="indiceVisor"
          :show-arrows="images.length > 1"
          hide-delimiter-background
          height="70vh"
        >
          <v-carousel-item
            v-for="imagen in images"
            :key="imagen.idImagen"
            :src="imagen.pathBase64"
            contain
          />
        </v-carousel>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="aviso"
      :timeout="colorAviso === 'error' ? -1 : 3000"
      :color="colorAviso"
      location="top"
    >
      {{ mensajeAviso }}
      <template #actions>
        <v-btn variant="text" size="small" @click="aviso = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import apisCompanys from '@/apis/apisCompanys.js';
import apisProducts from '@/apis/apisProducts';
import ComponentProduct from '@/components/ComponentProduct.vue';
import { ponerTitulo } from '@/router';

export default {
  name: 'ViewCompanyView',
  components: { ComponentProduct },
  data() {
    return {
      idCompany: null,
      company: null,
      isEnabled: false,
      // cargando | ok | noExiste | sinVigencia | sinVigenciaDueno
      estado: 'cargando',
      images: [],
      products: [],
      visorAbierto: false,
      indiceVisor: 0,
      aviso: false,
      mensajeAviso: '',
      colorAviso: 'success'
    };
  },
  computed: {
    /** Tope de 5: con menos salen todas, con mas se corta y se avisa. */
    imagenesGaleria() {
      return this.images.slice(0, 5)
    },
    fotosRestantes() {
      return Math.max(0, this.images.length - 5)
    },
    tieneWhatsapp() {
      return !!(this.company && this.company.receivesWhatsapp && this.company.whatsapp)
    },
    /**
     * Enlace de wa.me. Pide el numero en formato internacional y sin signos,
     * asi que se limpia y se le antepone el 57 cuando viene un celular
     * colombiano de 10 digitos escrito sin indicativo, que es como lo va a
     * escribir casi todo el mundo.
     */
    enlaceWhatsapp() {
      if (!this.tieneWhatsapp) {
        return ''
      }

      let numero = String(this.company.whatsapp).replace(/\D/g, '')

      if (numero.length === 10 && numero.startsWith('3')) {
        numero = '57' + numero
      }

      // El mensaje nombra a Bahiol a proposito: asi el negocio ve de donde le
      // llego el cliente, que es lo que sostiene que siga pagando
      const texto = `Hola ${this.company.company}, los vi en Bahiol y quiero preguntar por`
      return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`
    }
  },
  created() {
    this.idCompany = this.$route.params.id;

    apisCompanys.isEnabled(this.idCompany)
      .then(response => {
        this.isEnabled = response;

        if (!this.isEnabled) {
          this.averiguarPorQue()
          return
        }

        this.estado = 'ok'

        if (this.isEnabled) {
          apisCompanys.addCompanyView(this.idCompany)

          apisCompanys.getCompany(this.idCompany)
            .then(res => {
              this.company = res
              // El titulo lleva el nombre real de la empresa: es lo que se ve
              // en la pestana, al compartir el enlace y en los resultados de
              // Google. El router solo alcanza a poner el titulo base.
              ponerTitulo(res.company)
            })
        
          apisCompanys.ImagesCompany(this.idCompany)
            .then(res => {
              this.images = res;
            })

          apisProducts.getProductsCompany(this.idCompany)
            .then(res => {
              this.products = res;

              this.products.forEach(product => {
                apisProducts.ImagesProducts(product.idProduct)
                  .then(images => {
                    console.log('Fetched images for product', product.idProduct, images);
                    product.images = images;
                  })
                  .catch(error => {
                    console.error('Error fetching product images:', error);
                  });
              });

            })
            .catch(error => {
              console.error('Error fetching products:', error);
            });
        } 

      })
      .catch(error => {
        console.error('Error checking company status:', error);
      });

  },
  methods: {
    /**
     * isEnabled dice que no se puede ver, pero no por que. getCompany si lo
     * distingue: 404 es que la empresa no existe, 403 que existe y el que
     * mira no tiene permiso, y 200 solo le llega al dueno (o a un admin).
     * Con eso se puede decir algo util en vez de un aviso generico.
     */
    averiguarPorQue() {
      apisCompanys.getCompany(this.idCompany)
        .then(res => {
          // Solo el dueno recibe los datos de una empresa sin vigencia
          this.company = res
          this.estado = 'sinVigenciaDueno'
          ponerTitulo(res.company)
        })
        .catch(error => {
          this.estado = error?.response?.status === 404 ? 'noExiste' : 'sinVigencia'
        })
    },
    abrirImagen(indice) {
      this.indiceVisor = indice
      this.visorAbierto = true
    },
    callCompany() {
      if (this.company && this.company.phone) {
        window.location.href = `tel:${this.company.phone}`;
      }
    },
    abrirWhatsapp(){
      window.open(this.enlaceWhatsapp, '_blank', 'noopener')
    },
    toMapsDireccion(){
      const direccion = `${this.company.city} ${this.company.location} `;
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`;
      window.open(url, '_blank');
    },
    /**
     * Compartir la pagina de la empresa.
     *
     * En celular usa navigator.share, que abre el menu del sistema (WhatsApp,
     * Instagram, mensajes...). Es lo que la gente espera y es donde mas se
     * comparte un negocio.
     *
     * En escritorio ese API casi no existe, asi que se copia el enlace. Y como
     * navigator.clipboard SOLO funciona en contexto seguro (https o
     * localhost), queda de ultimo recurso el textarea con execCommand, que es
     * viejo pero funciona en http; si no, la pagina se serviria por http en
     * alguna prueba y el boton no haria absolutamente nada.
     */
    async compartir(){
      const url = window.location.href;
      const titulo = this.company ? this.company.company : 'Bahiol';
      const texto = this.company && this.company.description
        ? this.company.description.slice(0, 120)
        : 'Mira este negocio en Bahiol';

      if (navigator.share) {
        try {
          await navigator.share({ title: titulo, text: texto, url });
          return;
        } catch (error) {
          // AbortError es que el usuario cerro el menu a proposito: no es un
          // fallo y no hay que copiarle nada ni avisarle de nada.
          if (error && error.name === 'AbortError') {
            return;
          }
        }
      }

      if (await this.copiarEnlace(url)) {
        this.avisar('Enlace copiado', 'success');
      } else {
        this.avisar('No se pudo copiar el enlace', 'error');
      }
    },
    async copiarEnlace(url){
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(url);
          return true;
        } catch (error) {
          // Sigue al metodo viejo
        }
      }

      try {
        const area = document.createElement('textarea');
        area.value = url;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const copiado = document.execCommand('copy');
        document.body.removeChild(area);
        return copiado;
      } catch (error) {
        return false;
      }
    },
    avisar(mensaje, color){
      this.mensajeAviso = mensaje;
      this.colorAviso = color;
      this.aviso = true;
    }
  }
}
</script>
<style scoped>
/* ESTADO VACIO
   Ocupa la pagina en vez de ser una franja pegada arriba: si no hay empresa
   que mostrar, lo unico que hay que leer es esto. */
.estado-vacio {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 56px 16px 80px;
  min-height: 60vh;
}
.tarjeta-estado {
  max-width: 460px;
  width: 100%;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 14px;
  padding: 32px 28px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
/* La del dueno lleva borde azul: es la unica que pide una accion suya */
.tarjeta-dueno {
  border-color: #1976d2;
  border-top: 3px solid #1976d2;
}
.icono-estado {
  width: 62px;
  height: 62px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f5f9;
  color: #98a2b3;
}
.icono-aviso {
  background: #e8f1fb;
  color: #1976d2;
}
.titulo-estado {
  margin: 0 0 10px;
  font-size: 1.28rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
}
.texto-estado {
  margin: 0;
  font-size: 0.94rem;
  color: #667085;
  line-height: 1.5;
}
.nota-estado {
  margin: 14px 0 0;
  padding: 9px 12px;
  border-radius: 8px;
  background: #f2f5f9;
  font-size: 0.82rem;
  color: #667085;
}
.acciones-estado {
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.pagina {
  max-width: 960px;
  margin: 0 auto;
  background-color: #f5f5f5;
  min-height: 100vh;
}

/* CAROUSEL */
.carousel {
  width: 100%;
  height: 260px;
}

/* INFO */
.seccion-info {
  background: #fff;
  padding: 16px;
  margin-bottom: 8px;
}
.nombre-empresa {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
}
.chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.chip {
  background-color: #e3f2fd;
  color: #1565c0;
}
.chip-reservas {
  background-color: #e8f5e9;
  color: #2e7d32;
}
.aviso-reservas {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background-color: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
}
.icono-reservas {
  color: #2e7d32;
  flex-shrink: 0;
  margin-top: 1px;
}
.titulo-reservas {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2e7d32;
}
.texto-reservas {
  font-size: 0.83rem;
  color: #4a4a4a;
  line-height: 1.4;
}
.descripcion {
  color: #444;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 14px;
}
.acciones {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.btn-accion {
  flex: 1;
  min-width: 120px;
  color: #fff;
}
/* Compartir va en gris y no en color: llamar e ir al sitio son las dos
   acciones que le importan al negocio, y tres botones de color compiten */
.btn-compartir {
  background-color: #455a64;
}

/* Los de contacto no crecen: se quedan cuadrados y dejan el ancho a los dos
   que si llevan texto */
.btn-icono {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  color: #fff;
}
.btn-whatsapp {
  background-color: #25d366;
}

/* En celular no caben tres botones de 120px: quedaban Llamar y Ubicacion
   arriba y Compartir sola abajo, estirada a todo el ancho. Se quita el
   min-width y se aprieta el texto para que entren los tres en una fila. */
@media screen and (max-width: 599px) {
  .acciones {
    gap: 6px;
  }
  .btn-accion {
    min-width: 0;
    padding: 0 8px;
    font-size: 0.68rem;
    letter-spacing: 0;
  }
  .btn-accion .v-icon {
    font-size: 16px;
    margin-right: 3px !important;
  }
}

/* GALERIA */
.seccion-galeria {
  background: #fff;
  padding: 16px;
  margin-bottom: 8px;
}
.titulo-galeria {
  font-size: 1rem;
  font-weight: 600;
  color: #111;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1976d2;
}
/* Tamano fijo y alineadas a la izquierda: asi no crecen cuando hay pocas
   fotos ni se aplastan cuando hay cinco */
.grilla-galeria {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 86px;
  justify-content: start;
  gap: 6px;
  /* Las fotos van en fila y con tamano fijo, asi que a partir de cuatro o
     cinco no caben en un celular angosto. Se desplazan DENTRO de su franja:
     el que desborde debe ser este contenedor, nunca la pagina. */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  padding-bottom: 2px;
}
.celda-galeria {
  position: relative;
  aspect-ratio: 1 / 1;
  /* min-height 0 y overflow: dentro de un grid la imagen impone su alto
     natural y revienta la celda si no se corta */
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
}
.foto-galeria {
  width: 100%;
  height: 100%;
  transition: transform 0.2s ease;
}
.celda-galeria:hover .foto-galeria {
  transform: scale(1.04);
}
.mas-fotos {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
}

.tarjeta-visor {
  position: relative;
  background: #111;
}
.cerrar-visor {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

@media (min-width: 500px) {
  .grilla-galeria {
    grid-auto-columns: 104px;
  }
}

/* PRODUCTOS */
.seccion-productos {
  background: #fff;
  padding: 16px;
}
.titulo-productos {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1976d2;
}
.sin-productos {
  color: #888;
  text-align: center;
  padding: 24px 0;
  font-size: 0.9rem;
}
.grilla-productos {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

/* De base una sola columna: en celular el orden no cambia
   (carrusel, info, productos) */
.contenido-empresa {
  display: grid;
  /* minmax(0, 1fr) y no 1fr a secas: los items de una grilla traen
     min-width: auto, asi que una fila ancha (la galeria de fotos) estiraba la
     columna y con ella la pagina entera. Con el minmax la columna nunca pasa
     del ancho disponible y el contenido ancho se las arregla por dentro. */
  grid-template-columns: minmax(0, 1fr);
}

@media (min-width: 500px) {
  .grilla-productos {
    grid-template-columns: repeat(3, 1fr);
  }
  .carousel {
    height: 320px;
  }
}

/* En computador se parte en dos paneles: a la izquierda la empresa y sus
   botones de contacto, a la derecha el catalogo, que antes quedaba abajo
   del todo. Va despues del bloque de 500px para revertir la grilla a 2
   columnas dentro del panel derecho, que es mas angosto. */
@media (min-width: 900px) {
  .pagina {
    max-width: 1240px;
    padding: 12px;
  }
  .contenido-empresa {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 14px;
    align-items: start;
  }
  .grilla-productos {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
